import { Box, Card, CardBody, Divider, Flex, HStack, SimpleGrid, Spinner, Stack, Text, Tooltip, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { ReactNode } from "react";
import { adminGetAnalysisEntry } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import { colors } from "../../../styles";
import { IAdminAnalysisEntry } from "../../../types";

type PathKey = keyof IAdminAnalysisEntry["path"];

const PATH_ITEMS: { key: PathKey; label: string; color: string }[] = [
    { key: "list", label: "검색 목록", color: colors.blue },
    { key: "similar", label: "동일성분", color: colors.covalt },
    { key: "recommend", label: "유사 추천", color: colors.teal },
    { key: "keyword", label: "키워드", color: colors.link },
];

function safeNumber(value: number | undefined) {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatCount(value: number) {
    return Math.round(value).toLocaleString("ko-KR");
}

function formatRatio(value: number) {
    return `${safeNumber(value).toLocaleString("ko-KR", { maximumFractionDigits: 1 })}%`;
}

function formatShare(part: number, total: number) {
    if (total <= 0 || part <= 0) return "0%";
    const percent = (part / total) * 100;
    if (percent < 0.1) return "<0.1%";
    return `${(Math.round(percent * 10) / 10).toLocaleString("ko-KR", { maximumFractionDigits: 1 })}%`;
}

function ratioWidth(value: number, max: number) {
    if (max <= 0 || value <= 0) return "0%";
    return `${(value / max) * 100}%`;
}

function isAnalysisEntry(data: IAdminAnalysisEntry | undefined): data is IAdminAnalysisEntry {
    return Boolean(data?.count && data.ratio && data.path);
}

function AnalysisCard({ children }: { children: ReactNode }) {
    return (
        <Card
            width={"100%"}
            height={"100%"}
            border={`0.5px solid ${colors.g4}`}
            borderRadius={10}
            shadow={"md"}
            backgroundColor={colors.white}
        >
            <CardBody>{children}</CardBody>
        </Card>
    );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
    return (
        <Stack spacing={1}>
            <Text fontSize={18} fontWeight={700} color={colors.black}>{title}</Text>
            <Text fontSize={13} fontWeight={400} color={colors.g2}>{description}</Text>
        </Stack>
    );
}

function StatCard({ label, value }: { label: string; value: string }) {
    return (
        <AnalysisCard>
            <VStack width={"100%"} minH={"108px"} justifyContent={"center"} alignItems={"flex-start"} spacing={3}>
                <Text fontSize={16} fontWeight={500} color={colors.black}>{label}</Text>
                <Text fontSize={48} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {value}
                </Text>
            </VStack>
        </AnalysisCard>
    );
}

function PathCard({ path }: { path: IAdminAnalysisEntry["path"] | undefined }) {
    const rows = PATH_ITEMS.map((item) => ({
        ...item,
        value: safeNumber(path?.[item.key]),
    }));
    const total = rows.reduce((sum, row) => sum + row.value, 0);

    return (
        <AnalysisCard>
            <VStack align={"stretch"} spacing={4}>
                <HStack align={"baseline"} spacing={2}>
                    <Text fontSize={13} fontWeight={500} color={colors.g2}>전체</Text>
                    <Text fontSize={36} fontWeight={400} color={colors.black} lineHeight={"1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCount(total)}
                    </Text>
                </HStack>

                <Flex
                    height={"10px"}
                    width={"100%"}
                    backgroundColor={colors.g5}
                    borderRadius={"full"}
                    overflow={"hidden"}
                    role={"img"}
                    aria-label={rows.map((row) => `${row.label} ${formatShare(row.value, total)} ${formatCount(row.value)}`).join(", ")}
                >
                    {rows.map((row) => row.value > 0 ? (
                        <Tooltip
                            key={row.key}
                            label={`${row.label} ${formatShare(row.value, total)} (${formatCount(row.value)})`}
                            placement={"top"}
                            hasArrow
                            openDelay={200}
                        >
                            <Box flex={row.value} minW={0} height={"100%"} backgroundColor={row.color} />
                        </Tooltip>
                    ) : null)}
                </Flex>

                <VStack align={"stretch"} spacing={0.5}>
                    {rows.map((row) => (
                        <HStack
                            key={row.key}
                            spacing={3}
                            px={2}
                            py={1.5}
                            mx={-2}
                            borderRadius={"md"}
                            _hover={{ backgroundColor: colors.g5 }}
                        >
                            <HStack width={"96px"} flexShrink={0} spacing={2}>
                                <Box boxSize={"8px"} borderRadius={"full"} backgroundColor={row.color} flexShrink={0} />
                                <Text fontSize={13} fontWeight={500} color={colors.g1} noOfLines={1}>{row.label}</Text>
                            </HStack>
                            <Box flex={1} height={"8px"} backgroundColor={colors.g5} borderRadius={"full"} overflow={"hidden"}>
                                <Box
                                    height={"100%"}
                                    width={ratioWidth(row.value, total)}
                                    backgroundColor={row.color}
                                    borderRadius={"full"}
                                />
                            </Box>
                            <Box
                                flexShrink={0}
                                minW={"148px"}
                                textAlign={"right"}
                                fontSize={13}
                                fontWeight={700}
                                color={colors.black}
                                whiteSpace={"nowrap"}
                                sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                                {formatShare(row.value, total)}
                                <Text as={"span"} fontWeight={400} color={colors.g2}> ({formatCount(row.value)})</Text>
                            </Box>
                        </HStack>
                    ))}
                </VStack>
            </VStack>
        </AnalysisCard>
    );
}

export default function Entry() {
    const { data, isLoading, isError } = useQuery<IAdminAnalysisEntry>({
        queryKey: ["adminGetAnalysisEntry"],
        queryFn: adminGetAnalysisEntry,
    });

    const analysis = isAnalysisEntry(data) ? data : undefined;

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10} spacing={8}>
                <HStack justifyContent={"space-between"} alignItems={"center"}>
                    <Text fontSize={28} fontWeight={700} color={colors.black}>제품 분석</Text>
                </HStack>

                {isLoading ? (
                    <Spinner color={colors.blue} />
                ) : isError || !analysis ? (
                    <Text fontSize={14} fontWeight={400} color={colors.g2}>제품 분석 데이터를 불러오지 못했습니다.</Text>
                ) : (
                    <>
                        <Stack spacing={4}>
                            <SectionHeading
                                title={"상세 진입"}
                                description={"상세 페이지에 들어온 횟수와 사용자 수, 그리고 이어진 비율입니다."}
                            />
                            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={5}>
                                <StatCard label={"상세 페이지 진입 수"} value={formatCount(safeNumber(analysis.count.viewCount))} />
                                <StatCard label={"상세 페이지 진입 유저 수"} value={formatCount(safeNumber(analysis.count.userCount))} />
                                <StatCard label={"검색 유저 당 상세 진입 유저"} value={formatRatio(analysis.ratio.viewBySearchUser)} />
                                <StatCard label={"상세 진입 유저 당 온라인몰 클릭 유저"} value={formatRatio(analysis.ratio.clickByViewUser)} />
                            </SimpleGrid>
                        </Stack>

                        <Divider />

                        <Stack spacing={4}>
                            <SectionHeading
                                title={"진입 경로"}
                                description={"상세 페이지에 들어온 경로입니다. 막대는 전체 대비 비율입니다."}
                            />
                            <PathCard path={analysis.path} />
                        </Stack>
                    </>
                )}
            </Stack>
        </AdminOnly>
    );
}
